/**
 * FilesystemConnectDialog — DESKTOP-ONLY (Tauri) connect-flow for the
 * filesystem connector. Local-folder v1.
 *
 * Mirrors ``SpreadsheetConnectDialog`` 1:1 for the Radix Dialog chrome,
 * header, inline-error banner, footer buttons, and Ando visual language
 * (cream surface, ando-orange accents, text sizes). The connector is
 * single-vendor (``filesystem``) so ``AddAccountMenu`` fires the dialog
 * directly with ``selectedVendor === "filesystem"``.
 *
 * Flow (3 inline steps):
 *
 *   ┌── Step 1 "Cartella" ──┐  ┌── Step 2 "Anteprima" ──┐  ┌── Step 3 "ACL" ──┐
 *   │ Scegli cartella       │→ │ fsScan(connectorId=    │→ │ Utenti           │
 *   │ (native fsPickFolder) │  │  "preview") counts +   │  │ autorizzati      │
 *   │                       │  │ live onScanProgress    │  │ (comma emails)   │
 *   └───────────────────────┘  └────────────────────────┘  └──────────────────┘
 *                                                                  │ Conferma
 *                                                                  ▼
 *   createFilesystemConnector → fsSync({scan, apiBase, token, allowedUsers})
 *     → trackJob(jobId, "filesystem", {connector_id}) per jobId → success toast
 *
 * ALL Tauri work goes through ``fsBridge`` (never ``@tauri-apps/*`` here).
 * SMB / NFS / credentials are a LATER phase — NOT built in v1.
 *
 * authExpired handling: when ``fsSync`` returns ``authExpired === true`` (or
 * the ``onTokenExpired`` event fires mid-sync), we run the existing desktop
 * silent refresh (``refreshDesktopTokens`` + ``saveDesktopTokens``, mirroring
 * ``useDesktopOAuth.doRefresh``) and re-run ``fsSync`` ONCE with the fresh
 * token. If the refresh fails we surface "Riconnetti l'app".
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowRight, FolderOpen, HardDrive, Loader2, Network } from "lucide-react";

import * as React from "react";
import { ConnectorDialogShell } from "./ConnectorDialogShell";

import {
  type BaseConnection,
  Button,
  type CapabilityWizardSpec,
  cn,
  localize,
  toast,
} from "@ando/ui";

import { readAccessToken } from "@/features/auth/desktop/tokenStore";

import { createFilesystemConnector } from "../api/filesystemApi";
import {
  type ScanSummary,
  type SmbConfig,
  fsPickFolder,
  fsScan,
  fsScanSmb,
  isFilesystemConnectorAvailable,
  onScanProgress,
} from "../filesystem-desktop/fsBridge";
import { saveSmbCredentials } from "../filesystem-desktop/smbCredentialStore";
import {
  FS_DEFAULT_EXCLUDES,
  FS_FOLLOW_SYMLINKS,
  FS_MAX_FILE_SIZE_MB,
  FS_PERMISSION_MESSAGE,
  diagnoseEmptyScan,
  getScanExtensions,
  runFilesystemSmbSync,
  runFilesystemSync,
} from "../filesystem-desktop/syncFilesystem";
import { InlineErrorBanner } from "./InlineErrorBanner";
import { VendorBrandBadge } from "./VendorBrandBadge";

// ---------------------------------------------------------------------------
// Scan defaults: the canonical profile lives in ``syncFilesystem`` so the
// preview scan + the auto-sync hook share the exact same shape (DRY).
// ---------------------------------------------------------------------------

/** Throwaway connector id used for the pre-create preview scan. */
const PREVIEW_CONNECTOR_ID = "preview";

type Step = "folder" | "preview" | "acl";

/** Step-1 source mode: a local OS folder, or a programmatic network share. */
type SourceMode = "local" | "smb";

/** SMB form state → the `SmbConfig` wire shape + the share-relative scan root. */
interface SmbForm {
  readonly host: string;
  readonly share: string;
  readonly username: string;
  readonly password: string;
  readonly domain: string;
  /** Optional sub-folder within the share (`""` ⇒ whole share). */
  readonly subpath: string;
  /** Auth method: NTLM (workgroup / AD-with-NTLM) or Kerberos (Active Directory). */
  readonly authMode: "ntlm" | "kerberos";
  /** Kerberos realm (uppercased on the wire). */
  readonly realm: string;
  /** Kerberos KDC address (`host` or `host:port`). */
  readonly kdc: string;
}

const EMPTY_SMB_FORM: SmbForm = {
  host: "",
  share: "",
  username: "",
  password: "",
  domain: "",
  subpath: "",
  authMode: "ntlm",
  realm: "",
  kdc: "",
};

/** Shared input chrome for the SMB form (matches the ACL email field). */
const SMB_FIELD_CLASS =
  "h-9 w-full rounded-md border border-hairline bg-white/80 px-2.5 text-[13px] text-ink-1 placeholder:text-ink-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:opacity-60";

/** Derive the wire `SmbConfig`, the share-relative scan roots, the canonical
 * `smb://host/share/sub` logical root (the connector `base_paths` marker), and a
 * validity flag from the raw form. Single source so preview + confirm agree. */
function deriveSmb(form: SmbForm): {
  readonly config: SmbConfig;
  readonly roots: readonly string[];
  readonly logicalRoot: string;
  readonly valid: boolean;
} {
  const host = form.host.trim();
  const share = form.share.trim();
  const sub = form.subpath
    .trim()
    .replace(/^[/\\]+|[/\\]+$/g, "")
    .replace(/\\/g, "/");
  const isKerberos = form.authMode === "kerberos";
  const realm = form.realm.trim().toUpperCase();
  const kdc = form.kdc.trim();
  const config: SmbConfig = {
    host,
    share,
    username: form.username.trim(),
    password: form.password,
    domain: form.domain.trim() || null,
    authMode: form.authMode,
    realm: isKerberos ? realm || null : null,
    kdcAddress: isKerberos ? kdc || null : null,
  };
  // Kerberos also needs a realm + KDC + username; NTLM just needs host + share.
  const valid =
    host.length > 0 &&
    share.length > 0 &&
    (!isKerberos || (realm.length > 0 && kdc.length > 0 && config.username.length > 0));
  return {
    config,
    roots: [sub],
    logicalRoot: `smb://${host}/${share}${sub ? `/${sub}` : ""}`,
    valid,
  };
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface FilesystemConnectDialogProps {
  /** Controlled open state. */
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Spec — drives the dialog header/subtitle + brand badge. */
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  /** Called after a successful sync with the new connector id. */
  readonly onSuccess: (connectorId: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Last path segment (handles both ``/`` and ``\`` separators). */
function folderBaseName(path: string): string {
  const trimmed = path.replace(/[/\\]+$/, "");
  const idx = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Parse a comma-separated email field into a clean allow-list. */
function parseAllowedUsers(raw: string): readonly string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FilesystemConnectDialog: React.FC<FilesystemConnectDialogProps> = ({
  open,
  onOpenChange,
  spec,
  onSuccess,
}) => {
  const { i18n } = useLingui();
  const available = isFilesystemConnectorAvailable();

  const [step, setStep] = React.useState<Step>("folder");
  const [mode, setMode] = React.useState<SourceMode>("local");
  const [folderPath, setFolderPath] = React.useState<string | null>(null);
  const [smbForm, setSmbForm] = React.useState<SmbForm>(EMPTY_SMB_FORM);
  const [allowedUsersRaw, setAllowedUsersRaw] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const [scanSummary, setScanSummary] = React.useState<ScanSummary | null>(null);
  const [scanLoading, setScanLoading] = React.useState(false);
  const [liveScanCount, setLiveScanCount] = React.useState<number | null>(null);

  const [submitting, setSubmitting] = React.useState(false);
  const [syncProgress, setSyncProgress] = React.useState<{
    uploaded: number;
    total: number;
  } | null>(null);

  const submittingRef = React.useRef(false);

  const titleId = React.useId();

  // Reset on (re-)open.
  React.useEffect(() => {
    if (!open) return;
    setStep("folder");
    setMode("local");
    setFolderPath(null);
    setSmbForm(EMPTY_SMB_FORM);
    setAllowedUsersRaw("");
    setError(null);
    setScanSummary(null);
    setScanLoading(false);
    setLiveScanCount(null);
    setSubmitting(false);
    setSyncProgress(null);
    submittingRef.current = false;
  }, [open]);

  // ── Step 1 — folder picker ──────────────────────────────────────────
  const handlePickFolder = React.useCallback(() => {
    setError(null);
    void (async () => {
      try {
        const picked = await fsPickFolder();
        if (!picked) return; // user cancelled
        setFolderPath(picked);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : i18n._(msg`Selezione cartella non riuscita.`),
        );
      }
    })();
  }, []);

  // ── Step 1 → 2 — preview scan ───────────────────────────────────────
  // Extensions resolved ASYNC via ``getScanExtensions()`` (BE-served single
  // source with static fallback — Aug 26, 2026), so the preview scan and the
  // real sync can never disagree on what counts as "compatible".
  const buildScanConfig = React.useCallback(
    async (connectorId: string, roots: readonly string[]) => ({
      roots: [...roots],
      extensions: [...(await getScanExtensions())],
      excludeGlobs: FS_DEFAULT_EXCLUDES,
      maxFileSizeMb: FS_MAX_FILE_SIZE_MB,
      followSymlinks: FS_FOLLOW_SYMLINKS,
      connectorId,
    }),
    [],
  );

  // Specific, actionable validation message for the SMB form (so a disabled-less
  // "Avanti" can surface exactly what's missing instead of silently no-op'ing).
  const smbValidationError = React.useCallback((): string | null => {
    const { host, share } = smbForm;
    if (!host.trim() || !share.trim()) {
      return i18n._(msg`Inserisci host e nome della condivisione.`);
    }
    if (
      smbForm.authMode === "kerberos" &&
      (!smbForm.realm.trim() || !smbForm.kdc.trim() || !smbForm.username.trim())
    ) {
      return i18n._(msg`Per Active Directory servono realm, KDC e utente.`);
    }
    return null;
  }, [smbForm, i18n]);

  const handleGoToPreview = React.useCallback(() => {
    const smb = deriveSmb(smbForm);
    if (mode === "local" && !folderPath) {
      setError(i18n._(msg`Scegli prima una cartella.`));
      return;
    }
    if (mode === "smb" && !smb.valid) {
      setError(smbValidationError());
      return;
    }
    setError(null);
    setStep("preview");
    setScanSummary(null);
    setLiveScanCount(null);
    setScanLoading(true);

    let unlisten: (() => void) | undefined;
    void (async () => {
      try {
        // Local scan streams `fs-scan-progress`; the SMB preview is a single
        // directory listing (no per-file ticks) — the listener is harmless.
        unlisten = await onScanProgress((p) => {
          setLiveScanCount(p.matchedFiles);
        });
      } catch {
        // ignore — progress is best-effort.
      }
      try {
        const summary =
          mode === "smb"
            ? await fsScanSmb(await buildScanConfig(PREVIEW_CONNECTOR_ID, smb.roots), smb.config)
            : await fsScan(
                await buildScanConfig(PREVIEW_CONNECTOR_ID, folderPath ? [folderPath] : []),
              );
        setScanSummary(summary);
      } catch (err) {
        setError(err instanceof Error ? err.message : i18n._(msg`Anteprima non riuscita.`));
      } finally {
        setScanLoading(false);
        if (unlisten) unlisten();
      }
    })();
  }, [mode, folderPath, smbForm, buildScanConfig, smbValidationError, i18n]);

  // ── Confirm — create + sync ─────────────────────────────────────────
  // The sync itself (token read + 401 refresh-and-retry + per-file progress +
  // ``trackJob`` per cloud job) is the shared ``run*Sync`` path — the SAME the
  // auto-sync hook drives, so the wizard and background sync can't drift. The
  // result toasts are factored out so local + SMB report identically.
  const announceResult = React.useCallback(
    async (summary: { failedFiles: number; uploadedFiles: number; matchedFiles: number }) => {
      // Honest feedback — surface failed uploads + the "no compatible files"
      // case instead of a blanket success.
      if (summary.failedFiles > 0) {
        toast.error(
          i18n._(
            msg`Cartella collegata, ma ${summary.failedFiles} file non caricati. Riprova la sincronizzazione.`,
          ),
        );
      } else if (summary.uploadedFiles > 0) {
        toast.success(
          i18n._(msg`Cartella collegata · ${summary.uploadedFiles} file in caricamento`),
        );
      } else if (summary.matchedFiles === 0) {
        // Aug 26, 2026 — TCC can silently blank the scan on gated folders:
        // probe readability before blaming the contents (local roots only —
        // SMB roots are share-relative, the probe doesn't apply).
        const verdict =
          mode === "local" && folderPath
            ? await diagnoseEmptyScan([folderPath])
            : "no_compatible";
        if (verdict === "permission_denied") {
          toast.error(i18n._(FS_PERMISSION_MESSAGE), { duration: 12000 });
        } else if (verdict === "empty") {
          toast.warning(i18n._(msg`Cartella collegata, ma è vuota.`));
        } else {
          toast.warning(
            i18n._(
              msg`Cartella collegata, ma nessun file compatibile trovato (pdf, docx, xlsx, txt…).`,
            ),
          );
        }
      } else {
        toast.success(i18n._(msg`Cartella collegata`));
      }
    },
    [i18n, mode, folderPath],
  );

  const handleConfirm = React.useCallback(() => {
    const smb = deriveSmb(smbForm);
    if (mode === "local" && !folderPath) {
      setError(i18n._(msg`Scegli prima una cartella.`));
      return;
    }
    if (mode === "smb" && !smb.valid) {
      setError(smbValidationError());
      return;
    }
    void (async () => {
      if (submittingRef.current) return;
      setError(null);

      submittingRef.current = true;
      setSubmitting(true);
      setSyncProgress(null);

      const allowedUsers = parseAllowedUsers(allowedUsersRaw);
      const onProgress = (uploaded: number) =>
        setSyncProgress((prev) => ({
          uploaded,
          total: prev?.total ?? scanSummary?.matchedFiles ?? 0,
        }));

      try {
        if (mode === "smb") {
          const { connector_id } = await createFilesystemConnector({
            name: smb.config.share || folderBaseName(smb.logicalRoot),
            basePaths: [smb.logicalRoot],
            aclMode: "manual_mapping",
          });
          // Persist credentials to the OS keychain (Rust `keyring`) BEFORE syncing so the
          // background auto/nightly sync can re-authenticate the share.
          await saveSmbCredentials(connector_id, { ...smb.config, roots: smb.roots });
          const summary = await runFilesystemSmbSync({
            connectorId: connector_id,
            smb: smb.config,
            roots: smb.roots,
            allowedUsers,
            onProgress,
          });
          void announceResult(summary);
          onSuccess(connector_id);
        } else {
          const path = folderPath as string;
          const { connector_id } = await createFilesystemConnector({
            name: folderBaseName(path),
            basePaths: [path],
            aclMode: "manual_mapping",
          });
          const summary = await runFilesystemSync({
            connectorId: connector_id,
            roots: [path],
            allowedUsers,
            onProgress,
          });
          void announceResult(summary);
          onSuccess(connector_id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : i18n._(msg`Collegamento non riuscito.`));
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    })();
  }, [
    mode,
    folderPath,
    smbForm,
    allowedUsersRaw,
    scanSummary,
    announceResult,
    smbValidationError,
    onSuccess,
    i18n,
  ]);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <ConnectorDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._(msg`Collega una cartella locale`)}
      size="lg"
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="min-w-0 pr-8">
        <h2
          id={titleId}
          className="font-display font-semibold text-[15px] text-ink-1 leading-tight"
        >
          {step === "preview"
            ? i18n._(msg`Collega cartella · Anteprima`)
            : step === "acl"
              ? i18n._(msg`Collega cartella · Accessi`)
              : mode === "smb"
                ? i18n._(msg`Collega una cartella di rete`)
                : i18n._(msg`Collega una cartella locale`)}
        </h2>
        <p className="mt-1 text-[12px] text-ink-2 leading-snug">{localize(spec.subtitle, i18n)}</p>
      </div>

      {/* ── Desktop-only guard ──────────────────────────────────── */}
      {!available ? (
        <div className="mt-4 rounded-md border border-hairline bg-white/60 px-3 py-4 text-[13px] text-ink-2">
          <Trans>Disponibile solo nell'app desktop.</Trans>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {/* ── Step 1 — source (local folder or network share) ─── */}
          {step === "folder" ? (
            <>
              {/* Mode toggle — local OS folder vs programmatic SMB share. */}
              <fieldset className="grid grid-cols-2 gap-1.5 rounded-lg border border-hairline bg-white/50 p-1">
                <legend className="sr-only">{i18n._(msg`Tipo di origine`)}</legend>
                {[
                  { id: "local" as const, icon: HardDrive, label: msg`Cartella locale` },
                  { id: "smb" as const, icon: Network, label: msg`Cartella di rete` },
                ].map(({ id, icon: Icon, label }) => (
                  <label
                    key={id}
                    className={cn(
                      "flex cursor-pointer items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium text-[12px]",
                      "transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-ando-orange",
                      mode === id
                        ? "bg-ando-orange/12 text-ink-1 shadow-sm"
                        : "text-ink-3 hover:bg-black/[0.03] hover:text-ink-2",
                    )}
                  >
                    <input
                      type="radio"
                      name="fs-source-mode"
                      value={id}
                      checked={mode === id}
                      disabled={submitting}
                      onChange={() => {
                        setError(null);
                        setMode(id);
                      }}
                      className="sr-only"
                    />
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                    {i18n._(label)}
                  </label>
                ))}
              </fieldset>

              {mode === "local" ? (
                <>
                  <div className="flex items-start gap-3">
                    <VendorBrandBadge vendorId="filesystem" fallbackIcon={spec.icon} size="lg" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-ink-1 leading-snug">
                        <Trans>
                          Scegli una cartella sul tuo computer. Ando indicizza i documenti che
                          contiene (PDF, Word, Excel, PowerPoint, testo) senza spostarli.
                        </Trans>
                      </p>
                    </div>
                  </div>

                  {folderPath ? (
                    <div className="flex items-center gap-2.5 rounded-md border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-2.5">
                      <FolderOpen className="h-4 w-4 shrink-0 text-ando-orange" aria-hidden />
                      <span
                        className="min-w-0 flex-1 truncate text-[13px] text-ink-1"
                        title={folderPath}
                      >
                        {folderPath}
                      </span>
                    </div>
                  ) : null}

                  <div>
                    <Button type="button" variant="secondary" size="md" onClick={handlePickFolder}>
                      <FolderOpen aria-hidden />
                      {folderPath ? <Trans>Cambia cartella</Trans> : <Trans>Scegli cartella</Trans>}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ando-orange/12 text-ando-orange">
                      <Network className="h-4 w-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-ink-1 leading-snug">
                        <Trans>
                          Collega una cartella condivisa in rete (SMB/NAS). Ando legge i file
                          direttamente dalla rete — non serve montarla sul computer.
                        </Trans>
                      </p>
                    </div>
                  </div>

                  {/* Auth method — NTLM (workgroup) vs Kerberos (AD). */}
                  <fieldset className="grid grid-cols-2 gap-1.5 rounded-lg border border-hairline bg-white/50 p-1">
                    <legend className="sr-only">{i18n._(msg`Metodo di autenticazione`)}</legend>
                    {[
                      { id: "ntlm" as const, label: msg`Password` },
                      { id: "kerberos" as const, label: msg`Active Directory` },
                    ].map(({ id, label }) => (
                      <label
                        key={id}
                        className={cn(
                          "cursor-pointer rounded-md px-2.5 py-1.5 text-center font-medium text-[12px]",
                          "transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-ando-orange",
                          smbForm.authMode === id
                            ? "bg-ando-orange/12 text-ink-1 shadow-sm"
                            : "text-ink-3 hover:bg-black/[0.03] hover:text-ink-2",
                        )}
                      >
                        <input
                          type="radio"
                          name="fs-smb-auth"
                          value={id}
                          checked={smbForm.authMode === id}
                          disabled={submitting}
                          onChange={() => {
                            setError(null);
                            setSmbForm((f) => ({ ...f, authMode: id }));
                          }}
                          className="sr-only"
                        />
                        {i18n._(label)}
                      </label>
                    ))}
                  </fieldset>

                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                      <label htmlFor="smb-host" className="font-medium text-[11.5px] text-ink-2">
                        <Trans>Host / indirizzo</Trans>
                      </label>
                      <input
                        id="smb-host"
                        type="text"
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="nas.acme.local"
                        value={smbForm.host}
                        onChange={(e) => setSmbForm((f) => ({ ...f, host: e.target.value }))}
                        className={SMB_FIELD_CLASS}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                      <label htmlFor="smb-share" className="font-medium text-[11.5px] text-ink-2">
                        <Trans>Condivisione</Trans>
                      </label>
                      <input
                        id="smb-share"
                        type="text"
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="Documenti"
                        value={smbForm.share}
                        onChange={(e) => setSmbForm((f) => ({ ...f, share: e.target.value }))}
                        className={SMB_FIELD_CLASS}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                      <label
                        htmlFor="smb-username"
                        className="font-medium text-[11.5px] text-ink-2"
                      >
                        <Trans>Utente</Trans>
                      </label>
                      <input
                        id="smb-username"
                        type="text"
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="mario.rossi"
                        value={smbForm.username}
                        onChange={(e) => setSmbForm((f) => ({ ...f, username: e.target.value }))}
                        className={SMB_FIELD_CLASS}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                      <label
                        htmlFor="smb-password"
                        className="font-medium text-[11.5px] text-ink-2"
                      >
                        <Trans>Password</Trans>
                      </label>
                      <input
                        id="smb-password"
                        type="password"
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="••••••••"
                        value={smbForm.password}
                        onChange={(e) => setSmbForm((f) => ({ ...f, password: e.target.value }))}
                        className={SMB_FIELD_CLASS}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                      <label htmlFor="smb-domain" className="font-medium text-[11.5px] text-ink-2">
                        <Trans>Dominio (opzionale)</Trans>
                      </label>
                      <input
                        id="smb-domain"
                        type="text"
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="ACME"
                        value={smbForm.domain}
                        onChange={(e) => setSmbForm((f) => ({ ...f, domain: e.target.value }))}
                        className={SMB_FIELD_CLASS}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                      <label htmlFor="smb-subpath" className="font-medium text-[11.5px] text-ink-2">
                        <Trans>Sottocartella (opzionale)</Trans>
                      </label>
                      <input
                        id="smb-subpath"
                        type="text"
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="Progetti/2026"
                        value={smbForm.subpath}
                        onChange={(e) => setSmbForm((f) => ({ ...f, subpath: e.target.value }))}
                        className={SMB_FIELD_CLASS}
                      />
                    </div>
                  </div>

                  {smbForm.authMode === "kerberos" ? (
                    <div className="grid grid-cols-2 gap-2.5">
                      <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                        <label htmlFor="smb-realm" className="font-medium text-[11.5px] text-ink-2">
                          <Trans>Realm Kerberos</Trans>
                        </label>
                        <input
                          id="smb-realm"
                          type="text"
                          autoComplete="off"
                          disabled={submitting}
                          placeholder="ACME.LOCAL"
                          value={smbForm.realm}
                          onChange={(e) => setSmbForm((f) => ({ ...f, realm: e.target.value }))}
                          className={SMB_FIELD_CLASS}
                        />
                      </div>
                      <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
                        <label htmlFor="smb-kdc" className="font-medium text-[11.5px] text-ink-2">
                          <Trans>KDC (domain controller)</Trans>
                        </label>
                        <input
                          id="smb-kdc"
                          type="text"
                          autoComplete="off"
                          disabled={submitting}
                          placeholder="dc01.acme.local"
                          value={smbForm.kdc}
                          onChange={(e) => setSmbForm((f) => ({ ...f, kdc: e.target.value }))}
                          className={SMB_FIELD_CLASS}
                        />
                      </div>
                    </div>
                  ) : null}

                  <p className="text-[11px] text-ink-3 leading-snug">
                    {smbForm.authMode === "kerberos" ? (
                      <Trans>
                        Autenticazione Active Directory (Kerberos, chiavi AES). Le credenziali
                        restano nel tuo computer (vault cifrato), mai sul cloud.
                      </Trans>
                    ) : (
                      <Trans>
                        Le credenziali restano nel tuo computer (vault cifrato), mai sul cloud.
                      </Trans>
                    )}
                  </p>
                </>
              )}

              <InlineErrorBanner message={error} />

              <div className="mt-1 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={handleGoToPreview}
                  // SMB: keep enabled so the click surfaces the precise
                  // missing-field message (host/share vs realm/KDC); local
                  // needs a picked folder first.
                  disabled={mode === "local" ? folderPath === null : submitting}
                >
                  <Trans>Avanti</Trans>
                  <ArrowRight aria-hidden />
                </Button>
              </div>
            </>
          ) : null}

          {/* ── Step 2 — preview ──────────────────────────────── */}
          {step === "preview" ? (
            <>
              <div className="flex items-start gap-3 rounded-md border border-hairline bg-white/60 px-3 py-2.5">
                {mode === "smb" ? (
                  <Network
                    className="h-4 w-4 shrink-0 translate-y-px text-ando-orange"
                    aria-hidden
                  />
                ) : (
                  <FolderOpen
                    className="h-4 w-4 shrink-0 translate-y-px text-ando-orange"
                    aria-hidden
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate font-medium text-[13px] text-ink-1"
                    title={mode === "smb" ? deriveSmb(smbForm).logicalRoot : (folderPath ?? "")}
                  >
                    {mode === "smb"
                      ? smbForm.share.trim() || i18n._(msg`Condivisione`)
                      : folderPath
                        ? folderBaseName(folderPath)
                        : i18n._(msg`Cartella`)}
                  </p>
                  <p
                    className="mt-0.5 truncate text-[11.5px] text-ink-3"
                    title={mode === "smb" ? deriveSmb(smbForm).logicalRoot : (folderPath ?? "")}
                  >
                    {mode === "smb" ? deriveSmb(smbForm).logicalRoot : folderPath}
                  </p>
                </div>
              </div>

              <div
                aria-live="polite"
                className="rounded-md border border-hairline bg-white/40 px-3 py-2.5 text-[12px] text-ink-2"
              >
                {scanLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-ando-orange" aria-hidden />
                    <Trans>Analisi in corso…</Trans>
                    {liveScanCount !== null ? i18n._(msg` ${liveScanCount} file trovati`) : ""}
                  </span>
                ) : scanSummary ? (
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium text-ink-1">
                      <Trans>
                        {scanSummary.matchedFiles.toLocaleString("it-IT")} file da indicizzare
                      </Trans>
                    </span>
                    <span className="text-ink-3">
                      <Trans>{scanSummary.skippedFiles.toLocaleString("it-IT")} ignorati</Trans>
                    </span>
                    {scanSummary.totalBytes > 0 ? (
                      <span className="text-ink-3">{formatBytes(scanSummary.totalBytes)}</span>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-ink-3">
                    <Trans>Nessun dato di anteprima.</Trans>
                  </span>
                )}
              </div>

              <InlineErrorBanner message={error} />

              <div className="mt-1 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    setError(null);
                    setStep("folder");
                  }}
                >
                  <Trans>Indietro</Trans>
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={() => {
                    setError(null);
                    setStep("acl");
                  }}
                  disabled={scanLoading}
                >
                  <Trans>Avanti</Trans>
                  <ArrowRight aria-hidden />
                </Button>
              </div>
            </>
          ) : null}

          {/* ── Step 3 — ACL + confirm ────────────────────────── */}
          {step === "acl" ? (
            <>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="filesystem-allowed-users"
                  className="font-medium text-[11.5px] text-ink-2"
                >
                  <Trans>Utenti autorizzati</Trans>
                </label>
                <input
                  id="filesystem-allowed-users"
                  name="allowed_users"
                  type="text"
                  disabled={submitting}
                  placeholder="mario@acme.it, lucia@acme.it"
                  value={allowedUsersRaw}
                  onChange={(e) => setAllowedUsersRaw(e.target.value)}
                  className={cn(
                    "h-9 rounded-md border border-hairline bg-white/80 px-2.5 text-[13px] text-ink-1 placeholder:text-ink-3",
                    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                    "disabled:opacity-60",
                  )}
                />
                <p className="text-[11px] text-ink-3 leading-snug">
                  <Trans>Email separate da virgola — vuoto = solo te.</Trans>
                </p>
              </div>

              {submitting && syncProgress ? (
                <div
                  aria-live="polite"
                  className="flex items-center gap-2 rounded-md border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-2 text-[12px] text-ink-1"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-ando-orange" aria-hidden />
                  {syncProgress.total > 0
                    ? i18n._(msg`${syncProgress.uploaded}/${syncProgress.total} caricati`)
                    : i18n._(msg`${syncProgress.uploaded} caricati`)}
                </div>
              ) : null}

              <InlineErrorBanner message={error} />

              <div className="mt-1 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    setError(null);
                    setStep("preview");
                  }}
                  disabled={submitting}
                >
                  <Trans>Indietro</Trans>
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={handleConfirm}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      <Trans>Collegamento in corso…</Trans>
                    </>
                  ) : (
                    <>
                      <Trans>Collega</Trans>
                      <ArrowRight aria-hidden />
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}
    </ConnectorDialogShell>
  );
};

FilesystemConnectDialog.displayName = "FilesystemConnectDialog";
